import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import _, { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { fetchCustomer } from '@sophocles/api/customer';
import { fetchCustomerListWithFilters } from '@sophocles/entities/Customer';

const CustomerAutocomplete = ({
  name,
  value,
  size,
  error,
  helperText,
  required,
  fullWidth,
  disabled,
  onChange,
  onBlur,
  filters,
  label = null,
  sx = [],
  fixedData = null,
  onSelectedChange = null,
  variant = 'filled',
  hiddenLabel = false,
  disablePortal = false,
  ...extraProps
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const realLabel = label || t('customer.customer');

  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const selectedRef = useRef(null);
  const initialValueLoaded = useRef(false);
  const dropdownFetched = useRef(false);

  const selectedOption = useMemo(() => {
    const found = options.find(opt => opt.value === value);
    if (found) {
      selectedRef.current = found;
      return found;
    }
    // Preserve cached selection when search results don't include it
    if (selectedRef.current?.value === value) return selectedRef.current;
    return null;
  }, [options, value]);

  useEffect(() => {
    if (selectedOption) {
      onSelectedChange?.(selectedOption);
    }
  }, [selectedOption, onSelectedChange]);

  const saveDataToState = useCallback(inputData => {
    const newData = inputData.map(row => ({
      label: `${row.name}`,
      value: _.get(row, 'id', 0),
    }));
    // Ensure the cached selection stays in the list even if the API page doesn't include it
    if (selectedRef.current && !newData.some(opt => opt.value === selectedRef.current.value)) {
      newData.unshift(selectedRef.current);
    }
    setOptions(newData);
  }, []);

  const fetchData = useCallback(
    (search = '') => {
      setIsLoading(true);
      if (fixedData != null && Array.isArray(fixedData)) {
        saveDataToState(fixedData);
        setIsLoading(false);
        return;
      }
      dropdownFetched.current = true;
      dispatch(fetchCustomerListWithFilters(0, 50, filters || {}, search.trim(), ['name']))
        .then(res => {
          const response = res.toJS();
          if (_.has(response, 'results')) {
            saveDataToState(response.results);
          } else if (response instanceof Array) {
            saveDataToState(response);
          } else {
            saveDataToState([]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, fixedData, filters, saveDataToState],
  );

  const debouncedFetch = useMemo(() => debounce(search => fetchData(search), 300), [fetchData]);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  // If mounted with a pre-selected value, fetch that single customer first
  // so the label resolves immediately even if it's not in the first page.
  // Once the user interacts (opens dropdown / types), paginated fetches take over.
  useEffect(() => {
    if (value && !initialValueLoaded.current && !fixedData) {
      initialValueLoaded.current = true;
      setIsLoading(true);
      fetchCustomer(value)
        .then(customer => {
          const opt = { label: customer.name, value: customer.id };
          selectedRef.current = opt;
          setOptions([opt]);
        })
        .catch(() => {
          // Customer not found or permission error — fall back to paginated load
          fetchData('');
        })
        .finally(() => setIsLoading(false));
    } else if (!initialValueLoaded.current) {
      initialValueLoaded.current = true;
      fetchData('');
    }
  }, []);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      disablePortal={disablePortal}
      loading={isLoading}
      value={selectedOption}
      inputValue={inputValue}
      disabled={disabled}
      options={options}
      filterOptions={x => x}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      getOptionLabel={option => (option ? option.label : '')}
      sx={[...(Array.isArray(sx) ? sx : [sx])]}
      onOpen={() => {
        // First time opening the dropdown — load the full paginated list
        if (!dropdownFetched.current) {
          fetchData('');
        }
      }}
      onBlur={onBlur}
      onChange={(e, opt) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        const customerId = opt?.value;
        onChange(
          {
            ...e,
            preventDefault: () => {},
            stopPropagation: () => {},
            target: { id: `${name}-autocomplete-input`, name, value: customerId },
          },
          customerId,
          opt,
        );
      }}
      onInputChange={(e, value, reason) => {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }
        setInputValue(value);
        if (reason === 'input') {
          debouncedFetch(value);
          if (selectedOption != null) {
            onChange(e, null, null);
          }
        }
      }}
      renderOption={(props, option) => (
        <Box
          {...props}
          key={option.value}
          component="li"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start!important',
            padding: '8px',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          <Typography variant="body1" component="div">
            <Highlighter searchWords={[inputValue.trim()]} autoEscape textToHighlight={option.label} />
          </Typography>
        </Box>
      )}
      renderInput={params => {
        return (
          <TextField
            id={`${name}-autocomplete-input`}
            {...params}
            required={required || false}
            fullWidth={fullWidth || false}
            name={name}
            size={size || 'small'}
            variant={variant}
            hiddenLabel={hiddenLabel}
            label={hiddenLabel ? undefined : realLabel}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: isLoading ? <CircularProgress color="inherit" size={20} /> : params.InputProps.endAdornment,
            }}
          />
        );
      }}
    />
  );
};

export default CustomerAutocomplete;
